import * as bookmarksApi from "app/api/bookmarksApi";
import { getEventDesktop } from "app/api/eventsApi";
import Flag from "app/components/Flag/Flag";
import GoogleTagDataLayer from "app/components/GoogleTagDataLayer/GoogleTagDataLayer";
import IconSports from "app/components/Icon/components/IconSports/IconSports";
import {
  IconBookmark,
  IconCheck,
  IconFavoriteAlt,
} from "app/components/Icon/Icon";
import LoadingSpinner from "app/components/LoadingSpinner/LoadingSpinner";
import { MomentDate, MomentTime } from "app/components/Moment/Moment";
import { CountryHelper } from "app/helpers/countryHelper";
import {
  buildEventNavigationUrl,
  storeCurrentRouteAsPrevious,
} from "app/helpers/navigationUtils";
import { SessionHelper } from "app/helpers/sessionHelper";
import NavigationLogic from "app/logic/navigationLogic";
import { toUrlFriendlyName } from "app/screens/EventsScreen/screens/CompetitionScreenWrapper/nameHelper";
import type { EventType } from "app/types/eventType";
import { MarketTypes, SportsNameType } from "app/utils/const";
import isPublicView from "app/utils/publicViewUtils";
import isEmpty from "lodash/isEmpty";
import React, { useEffect } from "react";
import { FormattedMessage } from "react-intl";
import { connect } from "react-redux";
import { useHistory } from "react-router-dom";
import "./SearchList.scss";

type Props = {
  eventList: any;
  competitionList: any;
  isSearchListLoading: boolean;
  query: string;
  navigationTree: any;
  loadNavigation: (...args: Array<any>) => any;
  disableFilter: (...args: Array<any>) => any;
  disableTodayEarlyFilter: (...args: Array<any>) => any;
  updateBreadCrumb: (...args: Array<any>) => any;
  setFavouritesOnly: (...args: Array<any>) => any;
  loadBookmarks: (...args: Array<any>) => any;
  addFavouriteToList: (...args: Array<any>) => any;
  removeFromFavouritesList: (...args: Array<any>) => any;
  fetchSearchList: (...args: Array<any>) => any;
  resetCompetitions: (...args: Array<any>) => any;
  setSportsSelection: (...args: Array<any>) => any;
  stopCompetitionsPolling: (...args: Array<any>) => any;
  selectLeagues: (...args: Array<any>) => any;
  setUnsummarizedMarketsModalToOpen: (...args: Array<any>) => any;
  updateUnsummarizedMarketsModalEvent: (...args: Array<any>) => any;
  setSearchResultModalActive: (...args: Array<any>) => any;
  setEvent: (...args: Array<any>) => any;
  setFilters: (
    payload: { [key: string]: boolean | string | null | number }[]
  ) => void;
  setSportsSelectionListModel: (
    payload: {
      [key: string]: string | null;
    }[]
  ) => void;
};

type CompetitionData = {
  country: string;
  name: string;
  navigationTreeNodeId: number;
  sportName: string;
};

const SearchList: React.FC<Props> = ({
  eventList,
  competitionList,
  isSearchListLoading,
  query,
  navigationTree,
  loadNavigation,
  disableFilter,
  disableTodayEarlyFilter,
  updateBreadCrumb,
  setFavouritesOnly,
  loadBookmarks,
  addFavouriteToList,
  removeFromFavouritesList,
  fetchSearchList,
  resetCompetitions,
  setSportsSelection,
  stopCompetitionsPolling,
  selectLeagues,
  setUnsummarizedMarketsModalToOpen,
  updateUnsummarizedMarketsModalEvent,
  setSearchResultModalActive,
  setEvent,
  setSportsSelectionListModel,
  setFilters,
}) => {
  /**
   * Forces a re-render of the component.
   * This is achieved by calling the `updateState` function with an empty object as an argument.
   * This is linked to an existing bug in the original class file for euro desktop view
   */
  const [, updateState] = React.useState({});
  const forceRender = React.useCallback(() => updateState({}), []);

  useEffect(() => {
    if (isEmpty(navigationTree)) {
      loadNavigation();
    }
  }, [navigationTree, loadNavigation]);
  const history = useHistory();
  const onClickBookmark = (data: any, event: Record<string, any>) => {
    event.stopPropagation();

    if (!data.bookmark) {
      bookmarksApi.addBookmark(data.navigationTreeNodeId).then(resp => {
        data.bookmark = resp.id;
        forceRender();
      });
    } else {
      bookmarksApi.removeBookmark(data.bookmark).then(() => {
        delete data.bookmark;
        forceRender();
      });
    }
  };

  const updateNavigation = (data: any) => {
    // get single competition and update navigation model
    // find missing sport and country to build up navigation model
    // get sport
    let sport = navigationTree[0].nodes.filter(sport => {
      let dataSport = data.sportName || data.sportType;
      dataSport = dataSport.replace("_", " ");
      return sport.name.toUpperCase() === dataSport.toUpperCase();
    });

    let country = sport[0].nodes.filter(country => {
      const dataCountry = data.country || data.countryName;
      return country.name === dataCountry;
    });

    let competition: any = {
      id: data.navigationTreeNodeId || data.navigationNodeId,
      name: data.name,
      translatedName: data.name,
      type: "COMPETITION",
      "navigation-node-ids": [data.navigationTreeNodeId] || [
        data.navigationNodeId,
      ],
    };

    NavigationLogic.updateFilteringSingleCompetitions(
      [data.navigationTreeNodeId] || [data.navigationNodeId],
      sport[0],
      country[0],
      competition
    );
    resetCompetitions();
    disableFilter(true);
    disableTodayEarlyFilter(true);
    updateBreadCrumb(true);
    setFavouritesOnly(false);
  };

  const onClickEvent = (data: any) => {
    if (SessionHelper.isAsianRouteAndSessionView()) {
      stopCompetitionsPolling();
      getEventDesktop(data.eventId, false, false).then(resp => {
        let event = resp;
        event.competition.sportType = event.sportType;
        event.isActive = true;
        setSearchResultModalActive(true);
        setUnsummarizedMarketsModalToOpen(event);
      });
    } else {
      // Store the current route before navigation
      storeCurrentRouteAsPrevious();
      const url = buildEventNavigationUrl(data.eventId, data.nameWithoutDate);
      history.push(url);
    }
  };

  const onClickFavourite = (data: any, event: any) => {
    event.stopPropagation();

    if (!data.favourite) {
      addFavouriteToList(data.eventId, (resp: any) => {
        data.favourite = resp.id;
        forceRender();
      });
    } else {
      removeFromFavouritesList(data.eventId, (resp: any) => {
        delete data.favourite;
        forceRender();
      });
    }
  };

  const viewAllEventsInCompetition = (data: CompetitionData) => {
    if (SessionHelper.isAsianRouteAndSessionView()) {
      setFilters([
        {
          singleSelectedCompetitionId: data.navigationTreeNodeId,
        },
      ]);
      let sportName = data.sportName;
      if (sportName.toUpperCase() === SportsNameType.GOLF) {
        setSportsSelectionListModel([
          {
            selectedMarketType: MarketTypes.OUTRIGHT,
          },
        ]);
      } else {
        setSportsSelectionListModel([
          {
            selectedMarketType: null,
            selectedSportTimeFrame: null,
          },
        ]);
      }

      // Find the translated sport name from the navigation tree
      // because search/events-leagues endpoint doesn't return translatedName
      let sport = navigationTree[0]?.nodes.find(sport => {
        return sport.name === data.sportName;
      });
      setSportsSelectionListModel([
        {
          selectedSportName: data.sportName,
          translatedSportName: sport ? sport.translatedName : sportName,
        },
      ]);
      stopCompetitionsPolling();
      selectLeagues();
    } else {
      updateUnsummarizedMarketsModalEvent(null);
      const urlFriendlyName = toUrlFriendlyName(data.name);
      history.push(`/competition/${urlFriendlyName}`);
    }
  };

  const renderEventList = () => {
    if (eventList.length === 0) {
      return (
        <div className="search-list--no-results">
          <FormattedMessage tagName="span" id="search-noevents" />
        </div>
      );
    } else {
      return (
        <ul>
          {eventList.map((event: any, index: number) => {
            return (
              <li key={index}>
                <GoogleTagDataLayer
                  googleTagClickId={"clickSearchEvent"}
                  dlvEventName={"DLV-Click-Search-Event"}
                >
                  <div
                    onClick={() => onClickEvent(event)}
                    className="search-list__event"
                  >
                    <div className="search-list__event__info">
                      <IconSports name={event.sportType} />
                      <div className="search-list__event__name">
                        <div>{event.nameWithoutDate}</div>
                        <div className="search-list__event__date">
                          (
                          <MomentDate value={event.startDate} />
                          ,&nbsp;
                          <MomentTime value={event.startDate} />)
                        </div>
                      </div>
                    </div>
                    {event.favourite ? (
                      <div
                        onClick={clickEvent => {
                          onClickFavourite(event, clickEvent);
                        }}
                        className={`search-list__event__favorite-button ${
                          isPublicView() ? "hidden" : ""
                        }`}
                      >
                        <IconFavoriteAlt />
                      </div>
                    ) : (
                      <div
                        onClick={clickEvent => {
                          onClickFavourite(event, clickEvent);
                        }}
                        className={`search-list__event__favorite-button--not-added ${
                          isPublicView() ? "hidden" : ""
                        }`}
                      >
                        <IconFavoriteAlt />
                      </div>
                    )}
                  </div>
                </GoogleTagDataLayer>
              </li>
            );
          })}
        </ul>
      );
    }
  };

  const renderCompetitionList = () => {
    if (competitionList.length === 0) {
      return (
        <div className="search-list--no-results">
          <FormattedMessage tagName="span" id="search-noleagues" />
        </div>
      );
    } else {
      return (
        <ul>
          {competitionList.map((competition: any, index: number) => {
            return (
              <li key={index}>
                <div
                  onClick={() => viewAllEventsInCompetition(competition)}
                  className="search-list__competition"
                >
                  <div className="search-list__event__info">
                    <div className="search-list__competition__flag">
                      <Flag
                        code={CountryHelper.getCountryCode(competition.country)}
                        size={32}
                      />
                    </div>
                    <div className="search-list__competition__name">
                      {competition.name}
                    </div>
                  </div>
                  <button
                    className={`search-list__competition__bookmark-button ${
                      isPublicView() ? "hidden" : ""
                    }`}
                  >
                    {competition.bookmark ? (
                      <div
                        onClick={clickEvent => {
                          onClickBookmark(competition, clickEvent);
                        }}
                        className="bookmark"
                      >
                        <IconBookmark />
                        <IconCheck />
                      </div>
                    ) : (
                      <div
                        onClick={clickEvent => {
                          onClickBookmark(competition, clickEvent);
                        }}
                        className={`bookmark--not-added   `}
                      >
                        <IconBookmark />+
                      </div>
                    )}
                  </button>
                </div>
              </li>
            );
          })}
        </ul>
      );
    }
  };

  if (query.length === 0) {
    return (
      <div
        className={`search-list ${
          SessionHelper.isAsianRouteAndSessionView() ? "asia" : "europe"
        }`}
      >
        <div className="search-list__message">
          <FormattedMessage tagName="span" id="search-message" />
        </div>
      </div>
    );
  } else if (isSearchListLoading) {
    return (
      <div
        className={`search-list ${
          SessionHelper.isAsianRouteAndSessionView() ? "asia" : "europe"
        }`}
      >
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div
      className={`search-list ${
        SessionHelper.isAsianRouteAndSessionView() ? "asia" : "europe"
      }`}
    >
      <div className="search-list__title">
        <FormattedMessage tagName="span" id="search-events" />
      </div>
      {renderEventList()}
      <div className="search-list__title">
        <FormattedMessage
          tagName="span"
          id="collectionsports-title-competition"
        />
      </div>
      {renderCompetitionList()}
    </div>
  );
};

const mapStateToProps = (state: any) => ({
  eventList: state.searchListModel.eventList,
  competitionList: state.searchListModel.competitionList,
  isSearchListLoading: state.searchListModel.isSearchListLoading,
  query: state.searchListModel.query,
  navigationTree: state.navigationModel.navigationTree,
});

const mapDispatchToProps = (dispatch: any) => ({
  setEvent: (event: EventType) => {
    if (SessionHelper.isAsianRoute()) {
      dispatch.competitionListModel.updateEventInAsianCompetitionInList(event);
    } else {
      dispatch.competitionListModel.updateEventInCompetitionInList(event);
    }
  },
  setSearchResultModalActive: (active: any) =>
    dispatch.competitionListModel.setSearchResultModalActive(active),
  loadNavigation: () => dispatch.navigationModel.loadNavigation(),
  loadBookmarks: () => dispatch.bookmarkListModel.setBookmarkList(),
  disableFilter: (disable: any) =>
    dispatch.competitionListModel.disableFilter(disable),
  disableTodayEarlyFilter: (disable: any) =>
    dispatch.competitionListModel.disableTodayEarlyFilter(disable),
  updateBreadCrumb: (updateBreabcrumbState: any) =>
    dispatch.navigationModel.updateBreadCrumb(updateBreabcrumbState),
  setFavouritesOnly: (updateBreabcrumbState: any) =>
    dispatch.competitionListModel.setFavouritesOnly(updateBreabcrumbState),
  setSportsSelection: (sport: any, total: any) =>
    dispatch.sportsSelectionListModel.setSelectedSportInfo(sport, total),
  addFavouriteToList: (eventId: any, callback: any) =>
    dispatch.favouritesListModel.addFavouriteToList({
      eventId,
      callback,
    }),
  resetCompetitions: () => {
    dispatch.competitionListModel.resetCompetition();
  },
  removeFromFavouritesList: (eventId: any, callback: any) =>
    dispatch.favouritesListModel.removeFromFavouriteToList({
      eventId,
      callback,
    }),
  setFilters: payload => {
    dispatch.asianCompetitionListFiltersModel.setFilters(payload);
  },
  setSportsSelectionListModel: (payload: {
    [key: string]: boolean | string;
  }) => {
    dispatch.sportsSelectionListModel.set(payload);
  },

  setUnsummarizedMarketsModalToOpen: (event: EventType) => {
    dispatch.competitionListModel.setUnsummarizedMarketsModalToOpen(event);
  },
  updateUnsummarizedMarketsModalEvent: (event: EventType) => {
    dispatch.competitionListModel.updateUnsummarizedMarketsModalEvent(event);
  },
});

export default connect(mapStateToProps, mapDispatchToProps)(SearchList);
